import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import supabase from "../utils/supabase";
import useUserStore from "../stores/user";

interface LtiAuthResult {
  isAuthenticating: boolean;
  isAuthenticated: boolean;
  error: string | null;
}

export const useLtiAuth = (): LtiAuthResult => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const setUser = useUserStore((state) => state.setUser);
  const user = useUserStore((state) => state.user);

  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleLtiAuthentication = async () => {
      // Check if we're on an LTI-related page
      const currentPath = window.location.pathname;

      const isLtiPage =
        currentPath.includes("/lti/") || currentPath.includes("/auth/callback");

      const magicLink = searchParams.get("magicLink");
      const userData = searchParams.get("userData");
      const ltiToken = searchParams.get("ltik");

      // If no magic link and not on an LTI page, skip authentication
      if (!magicLink && !isLtiPage) {
        return;
      }

      // If user is already authenticated via the Zustand store, skip.
      if (user) {
        return;
      }

      // AuthCallback never calls setUser, so the store may be empty even when a
      // valid Supabase session exists (e.g. after the magic-link flow completes
      // and AuthCallback navigates back to the saved LTI path). Check the session
      // directly: if one is live, hydrate the store and skip the magic-link
      // navigation (the token is already consumed and we're already authed).
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const su = session.user;
        setUser({
          id: su.id,
          email: su.email ?? "",
          first_name: su.user_metadata?.first_name || su.user_metadata?.firstName || "",
          last_name: su.user_metadata?.last_name || su.user_metadata?.lastName || "",
          role: su.app_metadata?.role || su.user_metadata?.role || "student",
          school: su.user_metadata?.school ?? null,
          grade: su.user_metadata?.grade ?? null,
        });
        return;
      }

      // Save the current path for navigation after login (do this early)
      // Only save if we're on an LTI page AND we have a magic link (meaning user needs to authenticate)
      // AND we're not already on the callback page
      if (
        isLtiPage &&
        magicLink &&
        !localStorage.getItem("ltiReturnPath") &&
        !currentPath.includes("/auth/callback")
      ) {
        const currentPathWithSearch =
          window.location.pathname + window.location.search;
        localStorage.setItem("ltiReturnPath", currentPathWithSearch);
      }

      setIsAuthenticating(true);
      setError(null);

      try {
        // Use magic link for authentication
        if (magicLink) {
          console.log("Using magic link for LTI authentication");

          // Store user data if provided
          if (userData) {
            try {
              const parsedUserData = JSON.parse(userData);
              localStorage.setItem(
                "ltiUserData",
                JSON.stringify(parsedUserData)
              );
            } catch (e) {
              console.warn("Failed to parse user data:", e);
            }
          }

          // Store LTI token
          localStorage.setItem("authProvider", "lti");
          if (ltiToken) {
            localStorage.setItem("ltiToken", ltiToken);
          }

          // Redirect to magic link
          window.location.href = magicLink;
          return;
        }
      } catch (err) {
        console.error("LTI authentication error:", err);
        setError(err instanceof Error ? err.message : "Authentication failed");
      } finally {
        setIsAuthenticating(false);
      }
    };

    handleLtiAuthentication();
  }, [searchParams, user, setUser, navigate]);

  return {
    isAuthenticating,
    isAuthenticated: !!user,
    error,
  };
};
